import React from 'react';
import { Sheet, SheetContent } from '@/components/ui/Sheet';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Share2, Film, Trash2, Zap } from 'lucide-react';
import { cn } from '@/lib/utils';
import TiktokIcon from '@/components/ui/TiktokIcon';
import { Instagram } from 'lucide-react';

const FlowDetailsSheet = ({ flow, open, onOpenChange, onUpdate }) => {
  if (!flow) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent 
        side="right" 
        className="w-full sm:w-[400px] lg:w-[500px] bg-[#0D1117] border-l border-gray-800 p-0 overflow-y-auto"
      >
        <div className="h-full flex flex-col">
          <div className="p-6 border-b border-gray-800 bg-[#151A21]">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <Share2 className="w-5 h-5 text-purple-400" />
                <h2 className="text-xl font-semibold text-gray-100">
                  {flow.name}
                </h2>
              </div>
              <div className="flex items-center gap-2">
                {flow.scheduling === 'automated' && (
                  <Badge variant="secondary" className="bg-purple-500/10 text-purple-400 border-purple-500/20">
                    <Zap className="w-3.5 h-3.5 mr-1" />
                    Automated
                  </Badge>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 mt-4">
              <div className="flex -space-x-1">
                {flow.platforms.map(platform => (
                  <div 
                    key={platform}
                    className="w-6 h-6 rounded-full bg-gray-800 flex items-center justify-center ring-2 ring-gray-900"
                  >
                    {platform === 'tiktok' ? (
                      <TiktokIcon className="w-3.5 h-3.5 text-gray-300" />
                    ) : (
                      <Instagram className="w-3.5 h-3.5 text-gray-300" />
                    )}
                  </div>
                ))}
              </div>
              <Badge variant="secondary" className="bg-gray-800 text-gray-300">
                {flow.videos.length} videos
              </Badge>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            <div className="p-6 space-y-6">
              {flow.videos.length > 0 ? (
                <div className="space-y-3">
                  {flow.videos.map((videoId, index) => (
                    <div 
                      key={videoId}
                      className="bg-gray-800/50 rounded-lg p-3 flex items-center gap-3"
                    >
                      <div className="w-16 h-16 bg-gray-700 rounded-lg overflow-hidden">
                        {/* Video thumbnail would go here */}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-gray-200 font-medium truncate">
                          Video {index + 1}
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-gray-400 hover:text-red-400"
                        onClick={() => {
                          const updatedFlow = {
                            ...flow,
                            videos: flow.videos.filter(id => id !== videoId)
                          };
                          onUpdate(updatedFlow);
                        }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="bg-gray-800/50 rounded-full p-3 w-12 h-12 mx-auto mb-4 flex items-center justify-center">
                    <Film className="w-6 h-6 text-gray-400" />
                  </div>
                  <p className="text-gray-400">No videos added yet</p>
                  <p className="text-sm text-gray-500 mt-1">
                    Select videos from your library to add them to this flow
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default FlowDetailsSheet; 